import { Modal } from 'shared/ui/modal/modal';
import { ModalActions } from 'shared/ui/modal/modal-actions/modal-actions';
import { TerminateModalProps } from './terminate-modal.types';
import { useCachedUserInfo, useTranslation } from 'hooks';
import { useSubscriptions } from 'hooks/api/useSubscriptions';
import { Select } from 'shared/ui/select/select';
import { Option } from 'shared/ui/select/select.types';
import { useState } from 'react';
import cls from './terminate-modal.module.scss';
import Button from 'shared/ui/button/button';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { Filter } from 'shared/constants/color-filters';
import { useTerminateDomainsMutation } from 'store/api/userApiSlice';
import { snackbar } from 'shared/helpers/snackbar';
import { useNavigate } from 'react-router-dom';

export const TerminateModal: React.FC<TerminateModalProps> = (
  props: TerminateModalProps
) => {
  const { isOpen, onClose } = props;
  const { activeDomains } = useCachedUserInfo();
  const navigate = useNavigate();
  const { subscriptions, terminated } = useSubscriptions();
  const { t } = useTranslation();
  const [terminateDomains] = useTerminateDomainsMutation();
  const amoutToTerminate = terminated.length;
  const isMulti = amoutToTerminate > 1;

  const [selectedDomains, setSelectedDomains] = useState<Option[]>([]);
  const [error, setError] = useState<string>('');

  const assignedDomains = activeDomains?.filter(domain =>
    subscriptions?.some(sub => sub.license.id === domain.data?.license_id)
  );
  const selectOptions =
    assignedDomains?.map(domain => ({
      value: domain.domain,
      label: domain.domain,
    })) ?? [];

  const handleTerminate = async () => {
    const domainsToTerminate = selectedDomains.map(opt => opt.value);
    if (domainsToTerminate.length !== amoutToTerminate) {
      setError(
        t('terminateModalValidation', undefined, undefined, {
          count: amoutToTerminate,
        })
      );
      return;
    }
    setError('');
    terminateDomains(domainsToTerminate)
      .unwrap()
      .then(() => {
        snackbar(t('websitesTerminatedSuccess'));
        onClose();
      })
      .catch(() => {
        snackbar(t('websitesTerminatedError'));
      });
  };

  const handleBuy = () => {
    onClose();
    navigate('#');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} overlayClose={false}>
      <div className={cls.terminateModal}>
        <h3>
          {t(isMulti ? 'terminateModalTitlePlural' : 'terminateModalTitle')}
        </h3>
        <p className={cls.text}>
          {isMulti
            ? t('terminateModalBodyPlural', undefined, undefined, {
                count: amoutToTerminate,
              })
            : t('terminateModalBody')}
          <br />
          {t('terminateModalSubtext')}
          <br />
          <b>{t('terminateModalWarning')}</b>
        </p>
        <div>
          <p className={cls.label}>{t('terminateModalDropdownLabel')}</p>
          {isMulti ? (
            <Select
              isMulti={true}
              optionsList={selectOptions}
              selected={selectedDomains}
              onChange={val => {
                setSelectedDomains(val);
                setError('');
              }}
              toBeSelected={amoutToTerminate}
              errorText={error}
            />
          ) : (
            <Select
              isMulti={false}
              optionsList={selectOptions}
              selected={selectedDomains[0] ?? null}
              onChange={opt => {
                setSelectedDomains([opt]);
                setError('');
              }}
              errorText={error}
            />
          )}
        </div>
      </div>
      <ModalActions className={cls.buttons}>
        <Button
          onClick={handleBuy}
          theme='text'
          tone='default'
          className={cls.link}
        >
          <Icon pathToIcon={ICONS.newTab} filter={Filter.primary} />
          <span>{t('terminateModalBuy')}</span>
        </Button>
        <Button onClick={handleTerminate} theme='filled' tone='danger'>
          {t('terminateModalTerminate')}
        </Button>
      </ModalActions>
    </Modal>
  );
};
